import { Flex, type FlexProps } from '@chakra-ui/react';
import React from 'react';

import { CHIP_COLORS, INK } from '../../styles/tokens';

const INTER_STACK = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export type ChipTone = 'primary' | 'warn';

export type ChipProps = Omit<FlexProps, 'children'> & {
  /** Renders as its own bordered, hoverable, focusable pill (the default). Pass `false` to render as
   *  a flat, unstyled inline group instead -- for a caller that supplies its OWN outer pill chrome
   *  and only wants this component's row layout (e.g. nesting one `Chip` inside another). */
  readonly standalone?: boolean;
  /** Border/hover accent family -- `CHIP_COLORS`'s primary (purple) or warn (amber) pair. A pure
   *  visual tone switch; this component has no opinion on what the tone means to a caller (urgency,
   *  status, or anything else). */
  readonly tone?: ChipTone;
  /** Accessible name, applied only when `standalone` (an embedded chip has no `role`/`tabIndex` of
   *  its own for a label to attach to -- the caller's own wrapper owns the accessible name then). */
  readonly ariaLabel?: string;
  /** Overrides the default `data-testid="chip"` -- for a caller nesting more than one chip. */
  readonly testId?: string;
  readonly children?: React.ReactNode;
};

/**
 * Generic, reusable rounded "chip" pill shell: a bordered, hoverable, keyboard-focusable inline
 * group with a two-tone (primary/warn) accent, OR -- with `standalone={false}` -- just the flat
 * content row so a caller can nest it inside its own pill. Carries NO domain content of its own (no
 * icon, no text, no aria-label composition): every consumer supplies its own `children`. `Encounter`
 * is the current consumer -- it owns the icon, date/day text, concurrent-stay segment, and the
 * aging-days badge, and renders all of it as this component's children.
 *
 * FORWARDS ITS REF AND ANY UNNAMED PROPS to the underlying `Flex` -- this is required, not
 * decorative: `Encounter` renders this as the DIRECT child of a Chakra `Popover`'s
 * `trigger="hover"` `PopoverTrigger`, which `cloneElement`s `onMouseEnter`/`onMouseLeave`/
 * `aria-haspopup`/`aria-expanded`/a positioning `ref` etc. straight onto its child -- a component
 * that ignores unrecognized props (as a naive destructure-only `React.FC` would) would silently
 * drop every one of them and break hover-to-open entirely.
 */
export const Chip = React.forwardRef<HTMLDivElement, ChipProps>(function Chip(
  { standalone = true, tone = 'primary', ariaLabel, testId = 'chip', children, ...rest },
  ref
) {
  const accent = tone === 'warn' ? CHIP_COLORS.warn : CHIP_COLORS.primary;
  const accentFaint = tone === 'warn' ? CHIP_COLORS.warnFaint : CHIP_COLORS.primaryFaint;
  const restBorder = tone === 'warn' ? CHIP_COLORS.warnBorderSoft : CHIP_COLORS.primarySoft;

  return (
    <Flex
      ref={ref}
      as="span"
      role={standalone ? 'group' : undefined}
      data-testid={testId}
      tabIndex={standalone ? 0 : undefined}
      aria-label={standalone ? ariaLabel : undefined}
      display="inline-flex"
      align="center"
      gap="6px"
      borderRadius={standalone ? '999px' : undefined}
      borderWidth={standalone ? '1px' : undefined}
      borderStyle={standalone ? 'solid' : undefined}
      borderColor={standalone ? restBorder : undefined}
      pt={standalone ? '3px' : undefined}
      pr={standalone ? '9px' : undefined}
      pb={standalone ? '3px' : undefined}
      pl={standalone ? '6px' : undefined}
      bgImage={
        standalone
          ? `linear-gradient(135deg, ${CHIP_COLORS.primaryFaint}, ${CHIP_COLORS.surface})`
          : undefined
      }
      fontFamily={INTER_STACK}
      fontSize="11.5px"
      fontWeight={500}
      color={INK}
      _hover={
        standalone
          ? {
              borderColor: accent,
              bgImage: 'none',
              bg: accentFaint
            }
          : undefined
      }
      {...rest}
    >
      {children}
    </Flex>
  );
});
